import { useState, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { APIWeb } from '@/api'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import EmptyState from '@/components/EmptyState'
import FixedBottom from '@/components/FixedBottom'
import Ripple from '@/components/Ripple'
import SkeletonBox from '@/components/SkeletonBox'
import { normalizeAllowedKycTypes } from '@/pages/my/cardholder/kyc/types'

// 开卡参数类型
interface ApplyData {
  pay_type: string
  provider: string
  activationCode?: string
  address_id?: number
  product_id?: number
}

// 绑定已有卡参数类型
interface BindData {
  productId: number
  product?: ModelsCardProduct
  backgroundImage?: string
  cardBin?: string
}

// 路由状态类型
interface LocationState {
  applyData?: ApplyData
  product?: ModelsCardProduct
  bindData?: BindData
}

export default function SelectCardholder() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  // 从URL获取卡片类型和操作类型
  const cardType = searchParams.get('type') || 'electronic'
  const action = searchParams.get('action') || ''
  const isBindMode = action === 'bind'

  // 从 location.state 获取开卡参数和产品数据
  const locationState = location.state as LocationState | null
  const applyData = locationState?.applyData
  const product = locationState?.product
  const bindData = locationState?.bindData

  // 获取产品ID：优先从 state 获取，URL 参数 productId 作为兜底（防止刷新丢失 state）
  const productId = useMemo(() => {
    const fromUrl = searchParams.get('productId')
    const urlProductId = fromUrl ? Number(fromUrl) : undefined
    return product?.id ?? applyData?.product_id ?? bindData?.productId ?? (Number.isFinite(urlProductId) ? urlProductId : undefined)
  }, [product?.id, applyData?.product_id, bindData?.productId, searchParams])

  const [loading, setLoading] = useState(true)
  const [cardholders, setCardholders] = useState<CardholderKycListItem[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)

  // 支持的持卡人（is_supported 为 true）
  const supportedCardholders = useMemo(() => {
    return cardholders.filter(item => item.is_supported !== false)
  }, [cardholders])

  // 不支持的持卡人（is_supported 为 false）
  const unsupportedCardholders = useMemo(() => {
    return cardholders.filter(item => item.is_supported === false)
  }, [cardholders])

  // 获取持卡人列表（传入产品ID用于筛选符合开卡的持卡人）
  const fetchCardholders = useCallback(async () => {
    try {
      setLoading(true)
      const response = await APIWeb.getCardholderKycListV2({
        product_id: productId ? Number(productId) : undefined
      })
      const list = response?.list || []
      setCardholders(list)
      // 默认选中第一个支持的持卡人
      const firstSupported = list.find((item: CardholderKycListItem) => item.is_supported !== false)
      if (firstSupported?.kyc?.id) {
        setSelectedId(firstSupported.kyc.id)
      } else {
        setSelectedId(null)
      }
    } catch (error) {
      console.error('获取持卡人列表失败:', error)
    } finally {
      setLoading(false)
    }
  }, [productId])

  // 页面加载时获取数据
  useEffect(() => {
    fetchCardholders()
  }, [fetchCardholders])

  // 处理选择持卡人（仅支持选择 is_supported 为 true 的持卡人）
  const handleSelect = useCallback((id: number, isSupported: boolean) => {
    if (!isSupported) return
    setSelectedId(id)
  }, [])

  // 开卡/绑卡场景：添加持卡人时带产品 allowed_kyc_types（接口常为 JSON 字符串，需 normalize）
  const allowedKycTypesRaw = product?.allowed_kyc_types ?? bindData?.product?.allowed_kyc_types
  const allowedKycTypesForAdd = useMemo(
    () => normalizeAllowedKycTypes(allowedKycTypesRaw),
    [allowedKycTypesRaw]
  )

  const handleAddCardholder = useCallback(() => {
    const hasWhitelist = allowedKycTypesForAdd != null && allowedKycTypesForAdd.length > 0
    if (hasWhitelist) {
      navigate('/my/cardholder/add', { state: { allowed_kyc_types: allowedKycTypesForAdd } })
    } else {
      navigate('/my/cardholder/add')
    }
  }, [navigate, allowedKycTypesForAdd])

  // 处理下一步
  const handleNext = useCallback(() => {
    if (!selectedId) return

    if (isBindMode && bindData) {
      const selectedHolder = cardholders.find((item) => item.kyc?.id === selectedId)
      const kycFirstName = selectedHolder?.kyc?.first_name?.trim() ?? ''
      const kycLastName = selectedHolder?.kyc?.last_name?.trim() ?? ''

      // 绑定已有卡流程：跳转到绑定下游卡页面
      navigate('/card-v2/bind-downstream-card', {
        state: {
          kycId: selectedId,
          productId: bindData.productId,
          product: bindData.product,
          backgroundImage: bindData.backgroundImage,
          cardBin: bindData.cardBin,
          kycFirstName,
          kycLastName,
        }
      })
      return
    }

    if (!applyData) return

    // 将 kyc_id 添加到开卡参数中
    const finalApplyData = {
      ...applyData,
      kyc_id: selectedId
    }

    // 跳转到选择验证方式页面
    navigate(`/card-v2/apply-card/select-verification?type=${cardType}`, {
      state: { applyData: finalApplyData, product }
    })
  }, [selectedId, applyData, cardType, navigate, isBindMode, bindData, product, cardholders])

  // 渲染骨架屏
  const renderSkeleton = () => {
    return (
      <div className="bg-white border border-solid border-[#EAECF0] rounded-2xl p-4">
        <div className="flex items-center">
          <SkeletonBox className="w-12 h-12 rounded-full flex-shrink-0" />
          <div className="flex-1 ml-3">
            <SkeletonBox className="w-24 h-5 rounded" />
            <SkeletonBox className="w-40 h-4 rounded mt-2" />
          </div>
          <SkeletonBox className="w-6 h-6 rounded-full" />
        </div>
      </div>
    )
  }

  // 渲染持卡人卡片
  const renderCardholderItem = (item: CardholderKycListItem, isSupported: boolean) => {
    const kyc = item.kyc
    const isSelected = kyc?.id === selectedId
    // 拼接姓名
    const name = [kyc?.last_name, kyc?.first_name].filter(Boolean).join(' ') || '--'

    const cardContent = (
      <div
        className={`bg-white border border-solid rounded-2xl p-4 transition-all ${
          isSupported
            ? `cursor-pointer ${isSelected ? 'border-1 border-[var(--primary-color)]' : 'border border-[#EAECF0]'}`
            : '!bg-[#F9F9F9] border border-[#EAECF0] opacity-60 cursor-not-allowed'
        }`}
        onClick={() => kyc?.id && handleSelect(kyc.id, isSupported)}
      >
        <div className="flex items-center">
          {/* 左侧图标：支持时显示普通头像，不支持时显示带时钟图标的头像 */}
          <div className="relative w-12 h-12 rounded-full bg-[#F2F4F7] flex items-center justify-center flex-shrink-0">
            <img
              src={kyc?.identity_type === 'passport' ? '/icon/overseas.svg' : '/icon/mainland.svg'}
              alt=""
              className={`w-6 h-6 ${!isSupported ? 'opacity-60' : ''}`}
            />
            {/* {!isSupported && (
              <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#86909C] flex items-center justify-center">
                <img src="/images/card-v2/cardholder-clock.svg" alt="" className="w-3 h-3" />
              </div>
            )} */}
          </div>

          {/* 中间内容 */}
          <div className="flex-1 ml-3 min-w-0">
            <div
              className={`text-base font-medium ${isSupported ? 'text-[#1D2129]' : 'text-[#86909C]'}`}
            >
              {name}
            </div>
            <div className={`text-sm mt-1 ${isSupported ? 'text-[#86909C]' : 'text-[#C9CDD4]'}`}>
              {t('证件号')} {kyc?.identity_number || '--'}
            </div>
          </div>

          {/* 右侧选中图标（仅支持时显示） */}
          {isSupported && isSelected && (
            <div className="w-6 h-6 rounded-full bg-[var(--primary-color)] flex items-center justify-center flex-shrink-0">
              <img src="/images/card-v2/select-success.svg" alt="" />
            </div>
          )}
        </div>
      </div>
    )

    return (
      <Ripple key={kyc?.id} className="rounded-2xl" disabled={!isSupported}>
        {cardContent}
      </Ripple>
    )
  }

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-base font-medium">{t('选择持卡人')}</span>}
        rightChildren={
          cardholders.length < 5 ? (
            <Ripple className="rounded-full">
              <div
                className="w-10 h-10 flex items-center justify-center cursor-pointer border border-solid border-[#E8E8E8] rounded-full"
                onClick={handleAddCardholder}
              >
                  <img src="/icon/add-black.svg" alt="" className="w-5 h-5" />
              </div>
            </Ripple>
          ) : null
        }
      />

      <div className="px-5 pt-5 flex flex-col flex-1 pb-24">
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index}>{renderSkeleton()}</div>
            ))}
          </div>
        ) : cardholders.length === 0 ? (
          <EmptyState
            text={t('暂无可用持卡人')}
            icon={<img src="/images/my/icons/no-card-user.svg" alt="" className="w-32 h-auto" />}
          />
        ) : (
          <div className="space-y-3">
            {/* 支持的持卡人列表 */}
            {supportedCardholders.map(item => renderCardholderItem(item, true))}

            {/* 分隔线：存在不支持持卡人时显示 */}
            {unsupportedCardholders.length > 0 && (
              <div className="flex items-center justify-center gap-2 py-4">
                <div className="w-7 shrink-0 h-px bg-[#EAECF0]" />
                <span className="min-w-0 text-xs text-[#86909C] text-center">
                  {t('当前卡片暂不支持以下类型持卡人')}
                </span>
                <div className="w-7 shrink-0 h-px bg-[#EAECF0]" />
              </div>
            )}

            {/* 不支持的持卡人列表（灰色禁用样式） */}
            {unsupportedCardholders.map(item => renderCardholderItem(item, false))}
          </div>
        )}
      </div>

      {/* 底部按钮 */}
      {cardholders.length > 0 && (
        <FixedBottom className="bg-white">
          <CustomButton onClick={handleNext} disabled={!selectedId || (!isBindMode && !applyData)}>
            {t('下一步')}
          </CustomButton>
        </FixedBottom>
      )}
    </PageLayout>
  )
}

